import { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Optional label used in the fallback message, e.g. "Dashboard" */
  label?: string;
  /** Test hook; production defaults to hiding implementation details. */
  exposeErrorDetails?: boolean;
}

interface State {
  error: Error | null;
  correlationId: string | null;
}

function createErrorCorrelationId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function shouldExposeErrorDetails(): boolean {
  return import.meta.env.PROD !== true;
}

/**
 * Class-based error boundary that catches render-time errors thrown by any
 * child component tree. Displays a user-friendly fallback with a retry button
 * instead of a blank white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, correlationId: null };

  static getDerivedStateFromError(error: unknown): State {
    return {
      error: error instanceof Error ? error : new Error(String(error)),
      correlationId: createErrorCorrelationId(),
    };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Keep full diagnostics in telemetry/dev-tools while rendering a safe user message.
    console.error("[ErrorBoundary]", {
      correlationId: this.state.correlationId,
      error,
      componentStack: info.componentStack,
    });
  }

  private handleReset = () => {
    this.setState({ error: null, correlationId: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const label = this.props.label ? `${this.props.label} page` : "this page";
    const exposeDetails = this.props.exposeErrorDetails ?? shouldExposeErrorDetails();

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-800 mb-1">
          Something went wrong
        </h2>
        <p className="text-sm text-gray-500 mb-2 max-w-sm">
          An unexpected error occurred while rendering {label}. You can try
          again or refresh the page.
        </p>
        <p className="text-xs text-slate-500 font-mono bg-slate-50 border border-slate-100 rounded px-3 py-2 mb-5 max-w-lg break-all">
          Error reference: {this.state.correlationId ?? "unavailable"}
        </p>
        {exposeDetails ? (
          <p className="text-xs text-red-500 font-mono bg-red-50 border border-red-100 rounded px-3 py-2 mb-5 max-w-lg break-all">
            {error.message}
          </p>
        ) : null}
        <button
          onClick={this.handleReset}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Try again
        </button>
      </div>
    );
  }
}
